import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import ResponsiveTable from "../Competition/ResponsiveTable";
import VenueFormDialog from "./VenueFormDialog";
import MatchFormDialog from "./MatchFormDialog";
import { useApp } from "../../../context/AppContext";

export default function MatchesManager() {
  const navigate = useNavigate();
  const { clearSession } = useApp();
  const [searchParams] = useSearchParams();

  const view = searchParams.get("view") || "venues";

  const [venues, setVenues] = useState([]);
  const [matches, setMatches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const [venueDialogOpen, setVenueDialogOpen] = useState(false);
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [editingVenue, setEditingVenue] = useState(null);
  const [editingMatch, setEditingMatch] = useState(null);

  const [venuePage, setVenuePage] = useState(0);
  const [venueRowsPerPage, setVenueRowsPerPage] = useState(5);
  const [matchPage, setMatchPage] = useState(0);
  const [matchRowsPerPage, setMatchRowsPerPage] = useState(5);
  const [selectedCategoryId, setSelectedCategoryId] = useState("all");

  const request = useCallback(
    async (url, options = {}) => {
      const access = localStorage.getItem("access");

      const headers = {
        ...(options.body instanceof FormData
          ? {}
          : { "Content-Type": "application/json" }),
        ...(options.headers || {}),
      };

      if (access) headers.Authorization = `Bearer ${access}`;

      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (response.status === 401) {
        clearSession();
        navigate("/admin-login", { replace: true });
        throw new Error("Tu sesión expiró");
      }

      const contentType = response.headers.get("content-type") || "";
      const data = contentType.includes("application/json")
        ? await response.json()
        : null;

      if (!response.ok) {
        throw new Error(
          data?.error ||
            data?.detail ||
            "Ocurrió un error al procesar la solicitud"
        );
      }

      return data;
    },
    [clearSession, navigate]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const [venuesData, matchesData, categoriesData, teamsData] =
        await Promise.all([
          request("/matches/venues/"),
          request("/matches/list/"),
          request("/competition/categories/"),
          request("/competition/teams/"),
        ]);

      setVenues(Array.isArray(venuesData) ? venuesData : []);
      setMatches(Array.isArray(matchesData) ? matchesData : []);
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      setTeams(Array.isArray(teamsData) ? teamsData : []);
    } catch (error) {
      setMessage({ type: "error", text: error.message });
      setVenues([]);
      setMatches([]);
      setCategories([]);
      setTeams([]);
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setVenuePage(0);
  }, [venues.length]);

  useEffect(() => {
    setMatchPage(0);
  }, [matches.length, selectedCategoryId]);

  const filteredMatches = useMemo(() => {
    if (selectedCategoryId === "all") return matches;
    return matches.filter(
      (match) => String(match.category_id) === String(selectedCategoryId)
    );
  }, [matches, selectedCategoryId]);

  const venueColumns = useMemo(
    () => [
      {
        key: "name",
        label: "Sede",
        render: (row) => (
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Typography
              sx={{
                fontSize: "0.95rem",
                fontWeight: 700,
                color: "text.primary",
              }}
            >
              {row.name}
            </Typography>
          </Stack>
        ),
      },
      {
        key: "actions",
        label: "Acciones",
        render: (row) => (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ width: "100%" }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditRoundedIcon />}
              onClick={() => {
                setEditingVenue(row);
                setVenueDialogOpen(true);
              }}
            >
              Editar
            </Button>

            <Button
              color="error"
              variant="outlined"
              size="small"
              startIcon={<DeleteRoundedIcon />}
              onClick={() => handleDeleteVenue(row.id)}
            >
              Eliminar
            </Button>
          </Stack>
        ),
      },
    ],
    []
  );

  const matchColumns = useMemo(
    () => [
      {
        key: "match",
        label: "Partido",
        render: (row) => (
          <Stack spacing={0.35}>
            <Typography
              sx={{
                fontSize: "0.95rem",
                fontWeight: 700,
                color: "text.primary",
              }}
            >
              {row.team_local} vs {row.team_visitor}
            </Typography>

            <Typography
              sx={{
                fontSize: "0.82rem",
                color: "text.secondary",
                fontWeight: 600,
              }}
            >
              {row.category}
            </Typography>
          </Stack>
        ),
      },
      {
        key: "venue",
        label: "Sede",
        render: (row) => (
          <Typography
            sx={{
              fontSize: "0.92rem",
              color: "text.primary",
              fontWeight: 600,
            }}
          >
            {row.venue}
          </Typography>
        ),
      },
      {
        key: "date",
        label: "Fecha",
        render: (row) => (
          <Typography
            sx={{
              fontSize: "0.92rem",
              color: "text.primary",
            }}
          >
            {row.date}
          </Typography>
        ),
      },
      {
        key: "time",
        label: "Hora",
        render: (row) => (
          <Typography
            sx={{
              fontSize: "0.92rem",
              color: "text.primary",
            }}
          >
            {String(row.time || "").slice(0, 5)}
          </Typography>
        ),
      },
      {
        key: "status",
        label: "Estado",
        render: (row) => (
          <Typography
            sx={{
              fontSize: "0.9rem",
              fontWeight: 700,
              color:
                row.status === "live"
                  ? "error.main"
                  : row.status === "finished"
                  ? "success.main"
                  : row.status === "cancelled"
                  ? "text.secondary"
                  : "primary.main",
            }}
          >
            {row.status === "live"
              ? "En vivo"
              : row.status === "scheduled"
              ? "Programado"
              : row.status === "finished"
              ? "Finalizado"
              : row.status === "cancelled"
              ? "Cancelado"
              : row.status}
          </Typography>
        ),
      },
      {
        key: "score",
        label: "Marcador",
        render: (row) => (
          <Typography
            sx={{
              fontSize: "0.92rem",
              color: "text.primary",
              fontWeight: 700,
            }}
          >
            {`${row.score_local ?? 0} - ${row.score_visitor ?? 0}`}
          </Typography>
        ),
      },
      {
        key: "actions",
        label: "Acciones",
        render: (row) => (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ width: "100%" }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditRoundedIcon />}
              onClick={() => {
                setEditingMatch(row);
                setMatchDialogOpen(true);
              }}
            >
              Editar
            </Button>

            <Button
              color="error"
              variant="outlined"
              size="small"
              startIcon={<DeleteRoundedIcon />}
              onClick={() => handleDeleteMatch(row.id)}
            >
              Eliminar
            </Button>
          </Stack>
        ),
      },
    ],
    []
  );

  const handleSaveVenue = async (values) => {
    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      if (editingVenue) {
        await request(`/matches/venues/${editingVenue.id}/update/`, {
          method: "PUT",
          body: JSON.stringify(values),
        });

        setMessage({
          type: "success",
          text: "Sede actualizada correctamente",
        });
      } else {
        await request("/matches/venues/create/", {
          method: "POST",
          body: JSON.stringify(values),
        });

        setMessage({
          type: "success",
          text: "Sede creada correctamente",
        });
      }

      setVenueDialogOpen(false);
      setEditingVenue(null);
      await loadData();
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVenue = async (venueId) => {
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      await request(`/matches/venues/${venueId}/delete/`, {
        method: "DELETE",
      });

      setMessage({
        type: "success",
        text: "Sede eliminada correctamente",
      });

      await loadData();
    } catch (error) {
      setMessage({ type: "error", text: error.message });
      setLoading(false);
    }
  };

  const handleSaveMatch = async (values) => {
    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      if (editingMatch) {
        await request(`/matches/${editingMatch.id}/update/`, {
          method: "PUT",
          body: JSON.stringify(values),
        });

        setMessage({
          type: "success",
          text: "Partido actualizado correctamente",
        });
      } else {
        await request("/matches/create/", {
          method: "POST",
          body: JSON.stringify(values),
        });

        setMessage({
          type: "success",
          text: "Partido creado correctamente",
        });
      }

      setMatchDialogOpen(false);
      setEditingMatch(null);
      await loadData();
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMatch = async (matchId) => {
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      await request(`/matches/${matchId}/delete/`, {
        method: "DELETE",
      });

      setMessage({
        type: "success",
        text: "Partido eliminado correctamente",
      });

      await loadData();
    } catch (error) {
      setMessage({ type: "error", text: error.message });
      setLoading(false);
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Card
          elevation={0}
          sx={{
            width: "95%",
            borderRadius: "22px",
            border: "1px solid rgba(140,15,44,0.12)",
          }}
        >
          <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
            <Typography variant="h4" sx={{ mb: 2 }}>
              Gestión de calendario
            </Typography>

            {message.text && (
              <Alert
                severity={message.type === "success" ? "success" : "error"}
                onClose={() => setMessage({ type: "", text: "" })}
                sx={{ mb: 2 }}
              >
                {message.text}
              </Alert>
            )}

            {view === "venues" && (
              <>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.2}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", sm: "center" }}
                  sx={{ mb: 2 }}
                >
                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 700,
                      color: "text.primary",
                    }}
                  >
                    Sedes
                  </Typography>

                  <Button
                    variant="contained"
                    startIcon={<AddRoundedIcon />}
                    onClick={() => {
                      setEditingVenue(null);
                      setVenueDialogOpen(true);
                    }}
                  >
                    Nueva sede
                  </Button>
                </Stack>

                <ResponsiveTable
                  rows={venues}
                  columns={venueColumns}
                  page={venuePage}
                  rowsPerPage={venueRowsPerPage}
                  onPageChange={(_, nextPage) => setVenuePage(nextPage)}
                  onRowsPerPageChange={(event) => {
                    setVenueRowsPerPage(parseInt(event.target.value, 10));
                    setVenuePage(0);
                  }}
                  getRowId={(row) => row.id}
                  getCardTitle={(row) => row.name}
                  emptyText={
                    loading
                      ? "Cargando sedes..."
                      : "Todavía no has creado sedes"
                  }
                />
              </>
            )}

            {view === "matches" && (
              <>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.2}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", md: "center" }}
                  sx={{ mb: 2 }}
                >
                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 700,
                      color: "text.primary",
                    }}
                  >
                    Partidos
                  </Typography>

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<PlaceRoundedIcon />}
                      onClick={() => navigate("/admin/matches?view=venues")}
                    >
                      Administrar sedes
                    </Button>

                    <TextField
                      select
                      label="Filtrar categoría"
                      value={selectedCategoryId}
                      onChange={(e) => setSelectedCategoryId(e.target.value)}
                      sx={{
                        minWidth: {
                          xs: "100%",
                          sm: 220,
                        },
                      }}
                    >
                      <MenuItem value="all">Todas</MenuItem>

                      {categories.map((category) => (
                        <MenuItem key={category.id} value={String(category.id)}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    <Button
                      variant="contained"
                      startIcon={<AddRoundedIcon />}
                      onClick={() => {
                        setEditingMatch(null);
                        setMatchDialogOpen(true);
                      }}
                      disabled={!venues.length || !teams.length}
                    >
                      Nuevo partido
                    </Button>
                  </Stack>
                </Stack>

                <ResponsiveTable
                  rows={filteredMatches}
                  columns={matchColumns}
                  page={matchPage}
                  rowsPerPage={matchRowsPerPage}
                  onPageChange={(_, nextPage) => setMatchPage(nextPage)}
                  onRowsPerPageChange={(event) => {
                    setMatchRowsPerPage(parseInt(event.target.value, 10));
                    setMatchPage(0);
                  }}
                  getRowId={(row) => row.id}
                  getCardTitle={(row) =>
                    `${row.team_local} vs ${row.team_visitor}`
                  }
                  emptyText={
                    loading
                      ? "Cargando partidos..."
                      : venues.length
                      ? "Todavía no has creado partidos"
                      : "Primero crea al menos una sede"
                  }
                />
              </>
            )}
          </CardContent>
        </Card>
      </Box>

      <VenueFormDialog
        open={venueDialogOpen}
        onClose={() => {
          setVenueDialogOpen(false);
          setEditingVenue(null);
        }}
        onSubmit={handleSaveVenue}
        initialData={editingVenue}
        loading={saving}
      />

      <MatchFormDialog
        open={matchDialogOpen}
        onClose={() => {
          setMatchDialogOpen(false);
          setEditingMatch(null);
        }}
        onSubmit={handleSaveMatch}
        initialData={editingMatch}
        loading={saving}
        categories={categories}
        teams={teams}
        venues={venues}
      />
    </>
  );
}